import { refreshSession } from './api';
import { ACCESS_TOKEN_KEY, clearAuth, getAccessToken } from './auth';

const apiRoot = '/api';
const adminBase = apiRoot
  ? apiRoot.endsWith('/api/admin')
    ? apiRoot
    : apiRoot.endsWith('/api')
      ? `${apiRoot}/admin`
      : `${apiRoot}/api/admin`
  : '/api/admin';

export interface ProjectSummary {
  id: string;
  title: string;
  location: string;
  status: string;
  featured: boolean;
}

export interface ProjectDetails extends ProjectSummary {
  summary: string;
  description: string;
  projectType: string;
  completionDate?: string | null;
}

export interface JobSummary {
  id: string;
  title: string;
  location: string;
  isOpen: boolean;
}

export interface JobPostingDetails extends JobSummary {
  department: string;
  employmentType: string;
  summary: string;
  responsibilities: string;
  qualifications: string;
  closesAt?: string | null;
}

export interface ContactSummary {
  id: string;
  fullName: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  sourcePage: string;
  isResolved: boolean;
  assignedTo?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApplicationSummary {
  id: string;
  jobPostingId: string;
  applicantName: string;
  applicantEmail: string;
  coverLetter: string;
  resumeUrl: string;
  submittedAt: string;
  status: string;
}

export interface AnalyticsOverview {
  totalProjects: number;
  openJobs: number;
  contactMessages: number;
  jobApplications: number;
}

export interface HomePageServiceCard {
  title: string;
  description: string;
}

export interface HomePageTestimonial {
  quote: string;
  author: string;
}

export interface HomePageStat {
  label: string;
  value: string;
}

export interface HomePageCarouselSlide {
  src: string;
  alt: string;
  title: string;
  description: string;
}

export interface HomePageContent {
  heroEyebrow: string;
  heroTitle: string;
  heroCopy: string;
  primaryCtaText: string;
  primaryCtaLink: string;
  secondaryCtaText: string;
  secondaryCtaLink: string;
  heroImage: string;
  services: HomePageServiceCard[];
  testimonials: HomePageTestimonial[];
  partners: string[];
  stats: HomePageStat[];
  carouselSlides: HomePageCarouselSlide[];
  showHero: boolean;
  showServices: boolean;
  showCarousel: boolean;
  showTestimonials: boolean;
  showStats: boolean;
  showPartners: boolean;
  showAboutPage: boolean;
  showPortfolioPage: boolean;
  showServicesPage: boolean;
  showCareersPage: boolean;
  showNewsPage: boolean;
  showContactPage: boolean;
  showFaqPage: boolean;
  showQuotePage: boolean;
  showLegalPage: boolean;
}

const parseJsonResponse = async (response: Response) => {
  if (response.status === 204) {
    return undefined;
  }

  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return undefined;
  }

  return response.json();
};

const getAccessTokenFromStorage = async (): Promise<string | null> => {
  return getAccessToken() ?? sessionStorage.getItem(ACCESS_TOKEN_KEY);
};

const sendRequest = async (path: string, init: RequestInit = {}) => {
  const accessToken = await getAccessTokenFromStorage();

  const baseHeaders: Record<string, string> = {
    ...(accessToken ? {
      Authorization: `Bearer ${accessToken}`,
    } : {}),
  };

  if (!init.body || !(init.body instanceof FormData)) {
    baseHeaders['Content-Type'] = 'application/json';
  }

  if (init.headers) {
    if (init.headers instanceof Headers) {
      init.headers.forEach((value, key) => {
        baseHeaders[key] = value;
      });
    } else if (Array.isArray(init.headers)) {
      (init.headers as Array<[string, string]>).forEach(([key, value]) => {
        baseHeaders[key] = value as string;
      });
    } else {
      Object.assign(baseHeaders, init.headers as Record<string, string>);
    }
  }

  return fetch(`${adminBase}${path}`, {
    ...init,
    headers: baseHeaders as HeadersInit,
    credentials: init.credentials ?? 'include',
  });
};

const fetchJson = async (path: string, init: RequestInit = {}) => {
  let response = await sendRequest(path, init);

  if (response.status === 401) {
    try {
      await refreshSession();
      response = await sendRequest(path, init);
    } catch (error) {
      clearAuth();
      throw new Error('Request failed: 401');
    }
  }

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  return parseJsonResponse(response);
};

export const getProjects = async (): Promise<ProjectSummary[]> => {
  return fetchJson('/projects');
};

export const getProject = async (id: string): Promise<ProjectDetails> => {
  return fetchJson(`/projects/${id}`);
};

export const createProject = async (payload: Omit<ProjectDetails, 'id'>) => {
  return fetchJson('/projects', { method: 'POST', body: JSON.stringify(payload) });
};

export const updateProject = async (id: string, payload: Omit<ProjectDetails, 'id'>) => {
  return fetchJson(`/projects/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
};

export const deleteProject = async (id: string) => {
  return fetchJson(`/projects/${id}`, { method: 'DELETE' });
};

export const getJobs = async (): Promise<JobSummary[]> => {
  return fetchJson('/jobs');
};

export const getJob = async (id: string): Promise<JobPostingDetails> => {
  return fetchJson(`/jobs/${id}`);
};

export const createJob = async (payload: Omit<JobPostingDetails, 'id'>) => {
  return fetchJson('/jobs', { method: 'POST', body: JSON.stringify(payload) });
};

export const updateJob = async (id: string, payload: Omit<JobPostingDetails, 'id'>) => {
  return fetchJson(`/jobs/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
};

export const deleteJob = async (id: string) => {
  return fetchJson(`/jobs/${id}`, { method: 'DELETE' });
};

export const getApplications = async (): Promise<ApplicationSummary[]> => {
  return fetchJson('/applications');
};

export const updateApplicationStatus = async (id: string, status: string) => {
  return fetchJson(`/applications/${id}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
};

export const getContacts = async (): Promise<ContactSummary[]> => {
  return fetchJson('/contacts');
};

export interface AdminFileItem {
  id: string;
  fileName: string;
  contentType: string;
  size: number;
  url: string;
  uploadedAt: string;
}

export const getAdminFiles = async (): Promise<AdminFileItem[]> => {
  return fetchJson('/files');
};

export const getAdminFile = async (id: string): Promise<AdminFileItem> => {
  return fetchJson(`/files/${id}`);
};

export const uploadAdminFile = async (file: File): Promise<AdminFileItem> => {
  const formData = new FormData();
  formData.append('file', file);
  return fetchJson('/files', { method: 'POST', body: formData });
};

export const deleteAdminFile = async (id: string) => {
  return fetchJson(`/files/${id}`, { method: 'DELETE' });
};

export const updateContactMessage = async (
  id: string,
  payload: { isResolved: boolean; assignedTo?: string | null }
) => {
  return fetchJson(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
};

export const replyToContact = async (id: string, payload: { replyBody: string }) => {
  return fetchJson(`/contacts/${id}/reply`, { method: 'POST', body: JSON.stringify(payload) });
};

export const getAnalyticsOverview = async (): Promise<AnalyticsOverview> => {
  return fetchJson('/analytics/overview');
};

export interface ArticleSummary {
  id: string;
  slug: string;
  title: string;
  category: string;
  publishedAt: string;
}

export const getArticles = async (): Promise<ArticleSummary[]> => {
  return fetchJson('/articles');
};

export const getArticle = async (id: string) => {
  return fetchJson(`/articles/${id}`);
};

export const createArticle = async (payload: unknown) => {
  return fetchJson('/articles', { method: 'POST', body: JSON.stringify(payload) });
};

export const updateArticle = async (id: string, payload: unknown) => {
  return fetchJson(`/articles/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
};

export const deleteArticle = async (id: string) => {
  return fetchJson(`/articles/${id}`, { method: 'DELETE' });
};

export const getHomePageContent = async (): Promise<HomePageContent> => {
  return fetchJson('/homepage');
};

export const updateHomePageContent = async (payload: HomePageContent): Promise<void> => {
  await fetchJson('/homepage', { method: 'PUT', body: JSON.stringify(payload) });
};

export interface ContactInfo {
  address?: string;
  email?: string;
  phone?: string;
  socialLinks?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
    instagram?: string;
  };
  mapEmbedUrl?: string;
}

export const getContactInfo = async (): Promise<ContactInfo> => {
  return fetchJson('/contactinfo');
};

export const updateContactInfo = async (payload: ContactInfo): Promise<void> => {
  await fetchJson('/contactinfo', { method: 'PUT', body: JSON.stringify(payload) });
};

export interface TranslationSummary {
  languageCode: string;
  updatedAt: string;
}

export const getTranslationLanguages = async (): Promise<TranslationSummary[]> => {
  return fetchJson('/translations');
};

export const getTranslationFile = async (languageCode: string): Promise<Record<string, unknown>> => {
  return fetchJson(`/translations/${languageCode}`);
};

export const updateTranslationFile = async (languageCode: string, payload: unknown): Promise<void> => {
  await fetchJson(`/translations/${languageCode}`, { method: 'PUT', body: JSON.stringify(payload) });
};
